import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Factory, Flame, Tag, Sparkles, Trophy, MapPin, ArrowRight, Store, Truck } from 'lucide-react';
import { bc } from '../lib/api';
import CatalogCard from './CatalogCard';

function Row({ title, Icon, items, testid, to }) {
  if (!items?.length) return null;
  return (
    <section data-testid={testid} className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-base md:text-lg flex items-center gap-2"><Icon className="w-5 h-5 text-[#D35400]"/>{title}</h2>
        {to && <Link to={to} className="text-sm text-[#D35400] inline-flex items-center gap-1">See all <ArrowRight className="w-4 h-4"/></Link>}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
        {items.map(p => <div key={p.id} className="w-44 md:w-52 shrink-0 snap-start"><CatalogCard p={p}/></div>)}
      </div>
    </section>
  );
}

function Chips({ items, kind, testid, title, Icon }) {
  if (!items?.length) return null;
  return (
    <section data-testid={testid} className="space-y-3">
      <h2 className="font-heading font-bold text-base md:text-lg flex items-center gap-2"><Icon className="w-5 h-5 text-[#D35400]"/>{title}</h2>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {items.map(x => (
          <Link key={x.id} to={`/${kind}/${x.slug}`} data-testid={`${testid}-${x.slug}`} className="card-soft shrink-0 w-44 p-3 flex items-center gap-2">
            <span className="w-10 h-10 rounded-lg bg-[#FFF3E0] grid place-items-center overflow-hidden shrink-0">
              {x.logo_url ? <img src={x.logo_url} alt="" className="w-full h-full object-cover"/> : kind === 'brands' ? <b className="text-[#D35400]">{x.name[0]}</b> : <Factory className="w-4 h-4 text-[#D35400]"/>}
            </span>
            <span className="min-w-0"><span className="text-sm font-semibold flex items-center gap-1 truncate">{x.name}<BadgeCheck className="w-3.5 h-3.5 text-[#1B5E20] shrink-0"/></span>
              <span className="text-xs text-ink/60 truncate block">{kind === 'brands' ? (x.official ? 'Official store' : 'Verified brand') : `${x.city}, ${x.state}`}</span></span>
          </Link>
        ))}
      </div>
    </section>
  );
}

const CTAS = [
  ['/become-seller', 'Become a seller', 'Open your shop and sell across India', Store],
  ['/partner/register?type=brand_owner', 'Register your brand', 'Get an official verified brand store', BadgeCheck],
  ['/partner/register?type=manufacturer', 'Register as a manufacturer', 'Sell factory-direct, no middlemen', Factory],
];

export default function NationalHome() {
  const [d, setD] = useState(null);
  useEffect(() => { bc.home().then(setD).catch(() => setD({})); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-8" data-testid="national-home">
      <div className="rounded-2xl bg-ink text-white p-5 md:p-7 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFB74D] font-bold">BharatCart · All of India</div>
          <div className="font-heading font-black text-2xl md:text-3xl mt-1">Manufacturers → Brands → Distributors → Sellers → You</div>
          <div className="text-white/70 text-sm mt-1 flex items-center gap-1.5"><Truck className="w-4 h-4"/>Genuine products shipped by verified sellers, pan-India.</div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to="/catalog" data-testid="home-shop-india" className="btn-primary">Shop all of India</Link>
          <Link to="/brands" data-testid="home-shop-brands" className="btn-outline !bg-transparent !text-white !border-white/40">Shop by brand</Link>
        </div>
      </div>
      {d && (
        <>
          <Chips items={d.official_brands} kind="brands" testid="home-official-brands" title="Official brand stores" Icon={BadgeCheck}/>
          <Chips items={d.brands} kind="brands" testid="home-brands" title="Shop by brand" Icon={Tag}/>
          <Row title="Trending now" Icon={Flame} items={d.trending} testid="home-trending" to="/catalog"/>
          <Row title="Best deals & offers" Icon={Tag} items={d.deals} testid="home-deals" to="/catalog?sort=discount"/>
          <Row title="New arrivals" Icon={Sparkles} items={d.new_arrivals} testid="home-new" to="/catalog"/>
          <Row title="Bestsellers" Icon={Trophy} items={d.bestsellers} testid="home-bestsellers" to="/catalog"/>
          <Chips items={d.featured_manufacturers} kind="manufacturers" testid="home-manufacturers" title="Featured manufacturers" Icon={Factory}/>
          {d.regional_manufacturers?.length > 0 && (
            <section data-testid="home-regional" className="space-y-3">
              <h2 className="font-heading font-bold text-base md:text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-[#D35400]"/>Regional Indian manufacturers</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {d.regional_manufacturers.map(r => (
                  <div key={r.state} className="card-soft p-3"><div className="font-semibold text-sm mb-1">{r.state}</div>
                    {r.items.map(m => <Link key={m.id} to={`/manufacturers/${m.slug}`} className="block text-sm text-ink/70 hover:text-[#D35400] truncate">{m.name}</Link>)}</div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
      <section className="grid md:grid-cols-3 gap-3" data-testid="home-partner-ctas">
        {CTAS.map(([to, t, s, Icon]) => (
          <Link key={to} to={to} data-testid={`home-cta-${t.split(' ').pop().toLowerCase()}`} className="group rounded-2xl border-2 border-[#E67E22]/40 bg-[#FFF8EE] p-4 hover:border-[#E67E22] transition-colors">
            <Icon className="w-6 h-6 text-[#D35400]"/><div className="font-heading font-bold mt-2">{t}</div><div className="text-sm text-ink/70">{s}</div>
          </Link>
        ))}
      </section>
    </div>
  );
}
